import { Injectable } from '@angular/core';
import { Validators } from '@angular/forms';
import { PATIENT_LIMITS } from 'src/app/shared/app.constants';
import { WeightUnit } from '../../components/header/components/interface/interface';
import { StorageBrowser } from '../storage.service';
import { UserService } from '../user.service';

@Injectable({
  providedIn: 'root',
})
export class ConversionWeightService {
  public preferredWeightUnit: string;

  constructor( public userService: UserService, private storageBrowser: StorageBrowser) {
    const currUser = this.userService.currentUser.getValue() || {};
    this.preferredWeightUnit = currUser.preferredWeightUnit || WeightUnit.LB;
  }

  /**
   * Converts value from kg (server) to user's preferred unit.
   * Example: 70 kg → 154.32 lb (if preferred unit is lb)
   */
  fromServer(valueInKg: number): number {
    return this.preferredWeightUnit === WeightUnit.LB
      ? valueInKg * 2.20462
      : valueInKg;
  }

  /**
   * Converts value from user's preferred unit to kg (for sending to server).
   * Example: 154.32 lb → 70 kg (if preferred unit is lb)
   */
  toServer(valueInUserUnit: number): number {
    return this.preferredWeightUnit === WeightUnit.LB
      ? valueInUserUnit / 2.20462
      : valueInUserUnit;
  }

  /**
   * Optional: formatted value for display (e.g., "70 kg" or "154.32 lb")
   */
  formatFromServer(valueInKg: number): string {
    return `${this.formatValueFromServer(valueInKg)} ${this.preferredWeightUnit}`;
  }

  formatValueFromServer(valueInCm: number): string {
    const converted = this.fromServer(valueInCm);
    if (typeof converted !== 'number' || !Number.isFinite(converted)) {
      return '';
    }

    return converted.toFixed(2)
  }

  setPatientValidators(form: any, field: string): void {
    const limits = PATIENT_LIMITS.weight;
    const control = form.get(field);
    if (!control) return;

    // Values are always stored in kg, so validators operate on kg bounds.
    if (this.preferredWeightUnit === WeightUnit.LB) {
      // The tolerance keeps the exact lb bounds valid after the lb → kg conversion.
      const minKg = parseFloat((limits.LB.MIN * 0.453592 - 0.001).toFixed(3));
      const maxKg = parseFloat((limits.LB.MAX * 0.453592 + 0.001).toFixed(3));
      control.setValidators([Validators.min(minKg), Validators.max(maxKg)]);
    } else if (this.preferredWeightUnit === WeightUnit.KG) {
      control.setValidators([Validators.min(limits.KG.MIN), Validators.max(limits.KG.MAX)]);
    }
    control.updateValueAndValidity();
  }
}
